import { useEffect, useRef, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { useReactToPrint } from "react-to-print";
import orderServices from "../../services/orders/orderservice";

interface LineItem {
    productId: number;
    productName: string;
    categoryId: number;
    categoryName: string;
    hsnCode: string;
    quantity: number;
    taxableAmount: number;
    taxAmount: number;
    grossAmount: number;
    qtyUnit: string;
    gst: number;
    createdDate: string;
    ewayBillNo: string | null;
    vehicleNo: string | null;
    challanNo?: number;
}

interface CategorySummary {
    categoryId: number;
    categoryName: string;
    totalQuantity: number;
    totalTaxableAmount: number;
    totalTaxAmount: number;
    totalGrossAmount: number;
}

interface ChallanDetails {
    challanNo: number;
    challanType: string;
    generatedBy: string;
    fromAddress: string;
    toAddress: string;
    lineItems: LineItem[];
    categorySummaries: CategorySummary[];
    totalCount: number;
    hasEwayBill: boolean;
    vehicleNoFromDelivery: string;
}

interface Props {
    challanNo: number;
}

const ViewChallanDetails = ({ challanNo }: Props) => {
    const toast = useRef<Toast>(null);
    const printRef = useRef<HTMLDivElement>(null);
    const [challan, setChallan] = useState<ChallanDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [vesselDetails, setVesselDetails] = useState<any[]>([]);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `
      @page { 
        size: auto;
        margin: 10mm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact;
        }
        .no-print {
        display: none !important;
        }
        .p-datatable .p-datatable-thead > tr > th {
          background-color: #f8f9fa !important;
        }
        .font-bold {
          font-weight: bold !important;
        }
        .text-blue-600 {
          color: #2563eb !important;
        }
        .bg-blue-50 {
          background-color: #eff6ff !important;
        }/* Remove headers and footers */
        @page { margin: 0.5cm; }
        body { margin: 1.6cm; }
      }
    `
    });

    const formatDate = (dateString?: string) => {
        const date = dateString ? new Date(dateString) : new Date();
        return isNaN(date.getTime())
            ? "-"
            : date.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
            });
    };

    // ✅ Fetch challan details
   useEffect(() => {
    const fetchData = async () => {
        try {
        setLoading(true);

        // Fetch challan & vessel details in parallel
        const challanResponse = await orderServices.GetChallanDetailsByNo(challanNo);

            setChallan(challanResponse);

            // ⚡ vesselDetails comes inside challanResponse
            setVesselDetails(challanResponse.vesselDetails ?? []);
        } catch (error) {
        console.error("Error fetching challan or vessel details:", error);
        toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to fetch challan or vessel details.",
        });
        } finally {
        setLoading(false);
        }
    };

    fetchData();
    }, [challanNo]);


    const validLineItems = useMemo(
        () => challan?.lineItems?.filter((x) => Number(x.grossAmount) >= 0) || [],
        [challan]
    );

    const validCategorySummaries = useMemo(
        () => challan?.categorySummaries?.filter((x) => Number(x.totalGrossAmount) >= 0) || [],
        [challan]
    );

    const priceValueTotal = validCategorySummaries.reduce(
        (sum, c) => sum + (c.totalTaxableAmount || 0),
        0
    );
    const taxValueTotal = validCategorySummaries.reduce(
        (sum, c) => sum + (c.totalTaxAmount || 0),
        0
    );
    const grandTotal = validCategorySummaries.reduce(
        (sum, c) => sum + (c.totalGrossAmount || 0),
        0
    );

    const categoryRowGroupHeader = (rowData: LineItem) => (
        <strong>{rowData.categoryName}</strong>
    );

    const categoryRowGroupFooter = (rowData: LineItem) => {
        const category = validCategorySummaries.find(
            (c) => c.categoryId === rowData.categoryId
        );
        return (
            <div style={{ fontWeight: "bold" }}>
                {rowData.categoryName} Total:{" "}
                {category?.totalQuantity || 0} items (₹
                {(category?.totalGrossAmount || 0).toFixed(2)})
            </div>
        );
    };

    return (
        <div className="card p-4">
            <Toast ref={toast} />
            <div ref={printRef} >
            <div className="flex align-items-center justify-content-between mb-4">
                <h3 className="mb-0">{challan?.challanType} Details</h3>
                <Button
                    icon="pi pi-print"
                    label="Print Challan"
                    onClick={handlePrint}
                    className="p-button no-print"
                    disabled={loading || !challan}                  
                />
            </div>

            {/* === Addresses === */}
            <div className="grid mb-4">
                <div className="col-6">
                    <div className="p-3 border-round border-1 surface-border">
                        <h4>From:</h4>
                        <p>Desiri Naturals Private Limited</p>
                        <p style={{ whiteSpace: "pre-line" }}>
                            {challan?.fromAddress || "Not available"}
                        </p>
                        <p>GST #: {'29AAGCD5864J1Z4'}</p>
                    </div>
                </div>
                <div className="col-6">
                    <div className="p-3 border-round border-1 surface-border">
                        <h4>To:</h4>
                        <p>Desiri Naturals Private Limited</p>
                        <p style={{ whiteSpace: "pre-line" }}>
                            {challan?.toAddress || "Not available"}
                        </p>
                        <p>GST #: {'29AAGCD5864J1Z4'}</p>
                    </div>
                </div>
            </div>

            <div className="mb-4 p-3 border-round border-1 surface-border">
          <div className="grid">
            <h4>Purpose of material movement:</h4>
            <ol>
              <li>Below list of materials are moved against the customer orders placed on Desiri Naturals Pvt Ltd through online.</li>
              <li>Invoice will be generated after physical delivery to customer address mentioned below.</li>
            </ol>
          </div>
        </div>

            {/* === Challan Info === */}
            <div className="mb-4 p-3 border-round border-1 surface-border">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <strong>Challan # :</strong> {challan?.challanNo}
                    </div>
                    <div className="text-right">
                        <strong>Eway Bill # :</strong>{" "}
                        {challan?.hasEwayBill
                            ? challan?.lineItems[0]?.ewayBillNo || "-"
                            : "-"}
                    </div>
                    <div className="text-right">
                        <strong>Vehicle # :</strong>{" "}
                        {challan?.hasEwayBill
                            ? challan?.lineItems[0]?.vehicleNo || "-"
                            : challan?.vehicleNoFromDelivery}
                    </div>
                    <div>
                        <strong>Generated By :</strong> {challan?.generatedBy}
                    </div>
                    <div className="text-right">
                        <strong>Generated Date :</strong>{" "}
                        {formatDate(challan?.lineItems[0]?.createdDate)}
                    </div>
                </div>
            </div>            

            {/* === Product Table === */}
            <div className="mb-4">
                <h5>Product Details</h5>
                <DataTable
                    value={validLineItems}
                    rowGroupMode="subheader"
                    groupRowsBy="categoryName"
                    sortMode="single"
                    sortField="categoryName"
                    rowGroupHeaderTemplate={categoryRowGroupHeader}
                    rowGroupFooterTemplate={categoryRowGroupFooter}
                    stripedRows
                    showGridlines
                    className="p-datatable-sm"
                >
                    <Column field="productName" header="Product" style={{ minWidth: "18rem" }} />
                    <Column field="quantity" header="Qty" style={{ width: "5rem" }} />
                    <Column field="qtyUnit" header="Unit" style={{ width: "5rem" }} />
                    <Column field="hsnCode" header="HSN" style={{ width: "6rem" }} />
                    <Column header="Price Value (₹)" body={(r) => (r.taxableAmount || 0).toFixed(2)} style={{ width: "10rem", textAlign: "right" }} />
                    <Column header="Tax (₹)" body={(r) => (r.taxAmount || 0).toFixed(2)} style={{ width: "8rem", textAlign: "right" }} />
                    <Column header="Total (₹)" body={(r) => (r.grossAmount || 0).toFixed(2)} style={{ width: "8rem", textAlign: "right" }} />
                </DataTable>
            </div>

            {/* === Summary Table === */}
            <div className="p-3 border-round border-1 surface-border">
                <h4>Summary Totals</h4>
                <DataTable
                    value={[
                        ...validCategorySummaries,
                        {
                            categoryName: "Total",
                            totalTaxableAmount: priceValueTotal,
                            totalTaxAmount: taxValueTotal,
                            totalGrossAmount: grandTotal,
                        },
                    ]}
                    className="p-datatable-sm"
                    stripedRows
                    showGridlines
                    rowClassName={(r) =>
                        r.categoryName === "Total" ? "font-bold bg-blue-50" : ""
                    }
                >
                    <Column
                        field="categoryName"
                        header="Category"
                        body={(r) => (
                            <strong
                                className={
                                    r.categoryName === "Total" ? "text-blue-600" : ""
                                }
                            >
                                {r.categoryName}
                            </strong>
                        )}
                    />
                    <Column
                        header="Price Value (₹)"
                        body={(r) => (r.totalTaxableAmount || 0).toFixed(2)}
                        style={{ textAlign: "right" }}
                    />
                    <Column
                        header="Tax (₹)"
                        body={(r) => (r.totalTaxAmount || 0).toFixed(2)}
                        style={{ textAlign: "right" }}
                    />
                    <Column
                        header="Total (₹)"
                        body={(r) => (r.totalGrossAmount || 0).toFixed(2)}
                        style={{ textAlign: "right" }}
                    />
                </DataTable>
            </div>

            {/* === Vessel Details === */}
            <div className="p-3 border-round border-1 surface-border mt-4">
            <h4>Vessel Details</h4>
            {vesselDetails.length > 0 ? (
                <DataTable
                value={vesselDetails}
                className="p-datatable-sm"
                stripedRows
                showGridlines
                >
                <Column field="productName" header="Product Name" style={{ minWidth: "200px" }} />
                <Column field="totalQuantity" header="Total Quantity" style={{ width: "120px", textAlign: "center" }} />
                <Column field="vessels_5L" header="5L Vessels" style={{ width: "100px", textAlign: "center" }} />
                <Column field="vessels_3L" header="3L Vessels" style={{ width: "100px", textAlign: "center" }} />
                <Column field="vessels_2L" header="2L Vessels" style={{ width: "100px", textAlign: "center" }} />
                </DataTable>
            ) : (
                <div className="p-4 text-center">No vessel details available.</div>
            )}
            </div>
        </div>
      
    </div >
  );
};

export default ViewChallanDetails;
